import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { setTz } from "./format";

type User = { id: string; email: string; name: string; role: string; effectiveRole: string; permissions: string[]; tenantId: string };
type Tenant = { id: string; name: string; slug: string; timezone: string; settings: any };

type AuthState = {
  user: User | null | false;
  tenant: Tenant | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (perm: string) => boolean;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState>(null as any);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | false>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("tg_token")) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setTz(data.tenant?.timezone);
      setUser(data.user);
      setTenant(data.tenant);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email: string, password: string) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("tg_token", data.access_token);
    setTz(data.tenant?.timezone);
    setUser(data.user);
    setTenant(data.tenant);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("tg_token");
    setUser(false);
  };

  const can = (perm: string) => !!user && (user.permissions.includes("*") || user.permissions.includes(perm));

  return <AuthContext.Provider value={{ user, tenant, login, logout, can, refresh }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
